import { RegistrationProtocol } from '../../../user/registrationProtocol';
import { HubApiError } from '../../../domains/valueObject/SwitchUserProgressResponses';
import { Url } from '../url';
import { getUrlToLoginViaToken } from '../../../login/getUrlToLoginViaToken';
import { addLoginQueryParams } from '../../../utils/url';
import { GtmSender } from '../../../marketing/GtmSender';
import { systemEvents } from '../../../constants/gameEvents';
import { JRpcResponse } from '../jsonRpc/jsonRpcResponseFactory';
import { RegistrationResponse } from '@scorewarrior/rubens_web_lib/src/hub-api/protocol/registration-req';
import { getCurrentHubInstance } from '../../../constants/projectName';
import { HubApiBuilder } from '@scorewarrior/rubens_web_lib/src/hub-api/hub-api-builder';
import axios from 'axios';
import { Localization } from '../../../localization/localization';
import { getMarketingData } from '../marketing/marketing';

enum RegistrationErrorCode {
    NOT_REGISTERED = 17,
    TOO_MANY_REQUESTS = 429,
    WRONG_EMAIL = 16,
    EMAIL_EXISTS = 18,
    BAD_REQUEST = 400
}

export async function registrationV4(
    regData: RegistrationProtocol,
    isLpReg: boolean
): Promise<JRpcResponse<RegistrationResponse>> {
    try {
        const marketingResponse = await getMarketingData();

        const hubApiBuilder = new HubApiBuilder(Url.getHubClientRestapiUrl(), Url.getHubClientRestapiUrl());
        const hubApi = hubApiBuilder.getForGame(getCurrentHubInstance());

        let hubResponse: RegistrationResponse | HubApiError;
        switch (Number(regData.lp)) {
            case 4:
                hubResponse = await hubApi.registrationWithConfirmation(
                    window.app.deviceIdentifier,
                    regData.email,
                    regData.email_confirm,
                    window.app.config.current_entry_point,
                    window.app.config.locale,
                    regData.state,
                    regData.confidence,
                    marketingResponse
                );
                break;
            case 2:
                hubResponse = await hubApi.registrationEmailOnly(
                    window.app.deviceIdentifier,
                    regData.email,
                    window.app.config.current_entry_point,
                    window.app.config.locale,
                    regData.state,
                    regData.confidence,
                    marketingResponse
                );
                break;
            case 1:
            case 3:
            default:
                hubResponse = await hubApi.registrationWithPassword(
                    window.app.deviceIdentifier,
                    regData.email,
                    regData.password,
                    window.app.config.current_entry_point,
                    window.app.config.locale,
                    regData.state,
                    regData.confidence,
                    marketingResponse
                );
        }

        let url = getUrlToLoginViaToken(hubResponse.token, hubResponse.suid);
        url = addLoginQueryParams(url);
        await axios.get(url);

        GtmSender.fireClient(systemEvents.EMAIL_REG_SUCCESS);
        if (isLpReg) {
            GtmSender.fireClient(systemEvents.IS_LANDING_REG);
        }

        return {
            jsonrpc: '2.0',
            result: hubResponse
        };
    } catch (exception) {
        console.error(exception);
        window.Sentry.captureException(exception);

        if (exception.name === 'RpcReceivedError') {
            return {
                jsonrpc: '2.0',
                error: {
                    code: exception.code,
                    message: getErrorMessage(exception.code, exception.data),
                    data: exception.data
                }
            };
        }

        return {
            jsonrpc: '2.0',
            error: {
                code: exception.code ?? 500,
                message: exception.message,
                data: {}
            }
        };
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getErrorMessage(errorCode: number, errorData: any): string {
    let msg: string;
    switch (errorCode) {
        case RegistrationErrorCode.WRONG_EMAIL:
            msg = 'errors.email_wrong';
            break;
        case RegistrationErrorCode.EMAIL_EXISTS:
            msg = 'errors.already_reg';
            break;
        case RegistrationErrorCode.BAD_REQUEST:
            msg = errorData.error ?? 'errors.common_error';
            break;
        case RegistrationErrorCode.NOT_REGISTERED:
        case RegistrationErrorCode.TOO_MANY_REQUESTS:
        default:
            msg = 'errors.common_error';
    }

    return Localization._n(msg);
}
